import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/admin-api';
import toast from 'react-hot-toast';
import DetailSection from '../shared/DetailSection';
import Badge from '../shared/Badge';
import EmptyState from '../shared/EmptyState';
import AccessTable from '../shared/AccessTable';
import { getAccessLevelBadgeVariant } from '../../utils/accessHelpers';
import { getThemeBadgeVariant } from '../../constants/subThemes';

// User icon SVG path
const USER_ICON = 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z';

export default function ProductAccessTab({ productId }) {
  const [loading, setLoading] = useState(true);
  const [accessData, setAccessData] = useState(null);
  const [pendingRoleRemovals, setPendingRoleRemovals] = useState({});

  useEffect(() => {
    if (productId) {
      loadAccessData();
    }
  }, [productId]);

  const loadAccessData = async () => {
    try {
      setLoading(true);
      const data = await adminApi.products.getAccess(productId);
      setAccessData(data);
    } catch (error) {
      console.error('Failed to load access data:', error);
      toast.error('Failed to load user access information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-gray-400">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span>Loading access information...</span>
        </div>
      </div>
    );
  }

  if (!accessData || !accessData.users || accessData.users.length === 0) {
    return (
      <div className="p-8 max-w-5xl">
        <EmptyState
          icon={USER_ICON}
          title="No Users Found"
          subtitle="No users have access to this product"
        />
      </div>
    );
  }

  const columns = [
    { label: '', className: 'pb-0 pr-4' },
    { label: '', className: 'pb-0 px-4' }
  ];

  const renderRow = (user) => (
    <>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <div>
            <div className="font-medium text-white">
              {user.full_name || user.username}
            </div>
            <div className="text-xs text-gray-500">@{user.username}</div>
          </div>
          {!user.is_active && (
            <Badge variant="inactive" className="text-xs">
              Inactive
            </Badge>
          )}
        </div>
      </td>
      <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
        <Badge
          variant={getAccessLevelBadgeVariant(user.access_level)}
          className="text-xs"
        >
          {user.access_level.toUpperCase()}
        </Badge>
      </td>
    </>
  );

  const handleMarkRoleForRemoval = (userId, roleId) => {
    setPendingRoleRemovals(prev => {
      const userRemovals = prev[userId] || [];
      if (userRemovals.includes(roleId)) {
        // Already marked, unmark it
        return {
          ...prev,
          [userId]: userRemovals.filter(id => id !== roleId)
        };
      }
      // Mark for removal
      return {
        ...prev,
        [userId]: [...userRemovals, roleId]
      };
    });
  };

  const handleSaveRoleChanges = async (userId) => {
    const rolesToRemove = pendingRoleRemovals[userId] || [];
    if (rolesToRemove.length === 0) return;

    try {
      // Remove all marked roles
      await Promise.all(
        rolesToRemove.map(roleId => adminApi.users.removeRole(userId, roleId))
      );
      toast.success(`Removed ${rolesToRemove.length} role${rolesToRemove.length > 1 ? 's' : ''}`);

      // Clear pending removals for this user
      setPendingRoleRemovals(prev => {
        const newPending = { ...prev };
        delete newPending[userId];
        return newPending;
      });

      await loadAccessData();
    } catch (error) {
      console.error('Failed to remove roles:', error);
      toast.error('Failed to remove roles');
    }
  };

  const handleCancelRoleChanges = (userId) => {
    setPendingRoleRemovals(prev => {
      const newPending = { ...prev };
      delete newPending[userId];
      return newPending;
    });
  };

  const handleToggleDirectAccess = async (userId, hasDirectAccess) => {
    try {
      if (hasDirectAccess) {
        await adminApi.products.removeUser(productId, userId);
        toast.success('Direct access removed');
      } else {
        await adminApi.products.addUser(productId, userId, 'read');
        toast.success('Direct access granted');
      }
      await loadAccessData();
    } catch (error) {
      console.error('Failed to toggle direct access:', error);
      toast.error(`Failed to ${hasDirectAccess ? 'remove' : 'grant'} direct access`);
    }
  };

  const renderExpandedDetails = (user) => {
    // Check if user has direct product access
    const directAccess = user.access_reasons.find(reason => reason.source === 'direct');

    // Get role-based access
    const roleAccess = user.access_reasons.find(reason => reason.source === 'role');

    // Check if there are pending changes for this user
    const userPendingRemovals = pendingRoleRemovals[user.user_id] || [];
    const hasPendingChanges = userPendingRemovals.length > 0;

    return (
      <div className="px-4 pb-4 pt-2" onClick={(e) => e.stopPropagation()}>
        <div className="grid grid-cols-2 gap-6">
          {/* User Roles Section */}
          <div className="min-w-[225px]">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              User Roles
            </h4>
            {user.user_roles && user.user_roles.length > 0 ? (
              <div className="space-y-3">
                {/* Roles pills container */}
                <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700/50">
                  <div className="flex flex-wrap gap-2">
                    {user.user_roles.map((role) => {
                      const isMarkedForRemoval = userPendingRemovals.includes(role.id);
                      return (
                        <div
                          key={role.id}
                          className="group relative"
                        >
                          <Badge
                            variant={getThemeBadgeVariant(role.theme)}
                            className={`text-xs transition-all duration-200 ${
                              isMarkedForRemoval ? 'opacity-50 line-through' : ''
                            } group-hover:pr-7`}
                          >
                            {role.name}
                          </Badge>
                          <button
                            onClick={() => handleMarkRoleForRemoval(user.user_id, role.id)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 text-red-400 hover:text-red-300"
                            title={isMarkedForRemoval ? "Unmark for removal" : "Mark for removal"}
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Save/Cancel buttons */}
                  {hasPendingChanges && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-700/50">
                      <button
                        onClick={() => handleSaveRoleChanges(user.user_id)}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-colors"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => handleCancelRoleChanges(user.user_id)}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <span className="text-xs text-gray-400 ml-2">
                        {userPendingRemovals.length} role{userPendingRemovals.length > 1 ? 's' : ''} marked for removal
                      </span>
                    </div>
                  )}
                </div>

                {roleAccess && roleAccess.roles && (
                  <div className="text-xs text-gray-400">
                    Grants access via: {roleAccess.roles.join(', ')}
                  </div>
                )}
              </div>
            ) : (
              <span className="text-sm text-gray-500">No roles assigned</span>
            )}
          </div>

          {/* Direct Product Access Section */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              Direct Product Access
            </h4>
            <div className="bg-gray-800/30 rounded-lg px-3 py-3 border border-gray-700/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-300">Has Direct Access</span>
                <button
                  onClick={() => handleToggleDirectAccess(user.user_id, !!directAccess)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    directAccess ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      directAccess ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {directAccess && (
                <div className="space-y-1 mt-3 pt-3 border-t border-gray-700/50">
                  <div className="text-xs text-gray-400">
                    {directAccess.detail}
                  </div>
                  {directAccess.granted_at && (
                    <div className="text-xs text-gray-500">
                      Granted: {new Date(directAccess.granted_at).toLocaleDateString()}
                    </div>
                  )}
                  {directAccess.expires_at && (
                    <div className="text-xs text-gray-500">
                      Expires: {new Date(directAccess.expires_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 max-w-5xl select-none">
      <div className="space-y-6">
        <DetailSection
          title="Users With Product Access"
          subtitle={`${accessData.users.length} user${accessData.users.length !== 1 ? 's have' : ' has'} access`}
        >
          <AccessTable
            columns={columns}
            data={accessData.users}
            renderRow={renderRow}
            renderExpandedDetails={renderExpandedDetails}
            getRowKey={(user) => user.user_id}
          />
        </DetailSection>
      </div>
    </div>
  );
}
